/**
 * @fileoverview Mapbox GL map view used across system-web's department
 *               pages — renders a department boundary, style switcher,
 *               and (Phase 5.9) the fire hazard zone + active fire
 *               perimeter overlays.
 *               NOTE: The fire hazard and active fire overlays are currently
 *               HIDDEN from the UI and disabled by default per customer
 *               request.
 * @module system-web/components/DepartmentMapView
 *
 * Responsibilities:
 * - Initialize and own the Mapbox GL map instance's lifecycle (create on
 *   mount, destroy on unmount).
 * - Render the department boundary polygon and fit the viewport to it.
 * - Provide a map style switcher (satellite/streets/etc.).
 * - Render the fire hazard zone overlay and active fire perimeter overlay
 *   (Phase 5.9). These layers and their toggle button are currently HIDDEN
 *   and disabled by default (tied to FIRE_HAZARD_UI_ENABLED) per customer
 *   request.
 *
 * Dependencies:
 * - mapbox-gl.
 * - ./fireHazardLayers — hazard zone + active fire layer add/remove/
 *   visibility/pulse functions.
 * - ../hooks/useFireHazardOverlay / ../hooks/useActiveFirePerimeters —
 *   the sanctioned service → hook → component boundary for these two
 *   online-only reference-data overlays (see those hooks' fileoverviews).
 *
 * Architecture:
 * - Fire hazard source/layers are added BEFORE the boundary source/layers
 *   in registration order so the department boundary highlight always
 *   renders on top (Mapbox renders in add order).
 * - mapStyle changes (both from handleStyleChange and the mapStyle prop)
 *   trigger a full setStyle(), which clears all custom sources/layers —
 *   both style.load handlers re-add fire hazard + active fire + boundary
 *   in the same order as the initial 'load' handler.
 * - FIXED: the mapStyle-prop-sync effect used to include `activeStyle` in
 *   its own dependency array while ALSO being the thing that reads
 *   `mapStyle === activeStyle` to decide whether to act — since
 *   handleStyleChange (the in-map style switcher) sets activeStyle
 *   without updating the mapStyle prop, that self-triggered a SECOND run
 *   of this same effect immediately after every manual style change, and
 *   the stale mapStyle prop (still the OLD value) no longer equaled the
 *   NEW activeStyle, so the effect proceeded to setStyle() the map BACK
 *   to the old prop value — reverting the user's own selection one
 *   render later. This was a real, actively-triggering bug (not just a
 *   hypothetical "if a future consumer forgets to sync mapStyle" case),
 *   made more expensive by this phase's fire-hazard sources now being
 *   re-added on every extra setStyle() churn. Fixed with
 *   lastSyncedMapStylePropRef, which distinguishes "the mapStyle PROP
 *   itself changed" from "activeStyle changed" (the latter alone must be
 *   a no-op for this effect).
 * - FIXED (regression in the fix above): lastSyncedMapStylePropRef was
 *   originally updated BEFORE checking whether the map actually
 *   exists/is ready — so a mapStyle prop change arriving while the map
 *   was still starting up (mapRef.current null during the async
 *   MAPBOX_TOKEN resolution window that gates whether the map container
 *   even renders) got marked "already synced" without ever calling
 *   setStyle(), and was then permanently lost: none of this effect's
 *   other dependencies change again once the map becomes ready, so it
 *   never got a second chance. Fixed by gating the entire effect (before
 *   touching the ref) on `mapRef.current && mapReady`, with `mapReady`
 *   added to the dependency array so this effect re-evaluates once the
 *   map transitions from not-ready to ready, using whatever mapStyle
 *   value is current at that point.
 *
 * @component DepartmentMapView
 * Purpose: Renders a department's location on a Mapbox map with boundary,
 *   style switching, and fire hazard/active-fire overlays.
 *   NOTE: The fire hazard and active fire overlays are currently HIDDEN
 *   from the UI and disabled by default (tied to FIRE_HAZARD_UI_ENABLED) per
 *   customer request.
 * Props: center, zoom, mapStyle, boundary, onCenterChange, onZoomChange,
 *   onStyleChange, className — see DepartmentMapViewProps below.
 * Behavior: Imperative Mapbox GL integration — owns the raw map instance
 *   via refs; reacts to prop changes via targeted useEffects.
 * Data Source: hybrid — boundary/center/zoom/style are props (system-web
 *   page state); fire hazard/active-fire overlay data is fetched live via
 *   the hooks above (online-only exception, not Dexie-backed).
 * Constraints: requires MAPBOX_TOKEN (system-web runtime config) — renders
 *   a placeholder when absent.
 * Performance: fire hazard overlay refetches are debounced 300ms on
 *   moveend/zoomend; active fire overlay refetches on a fixed 5-minute
 *   interval (not viewport-scoped, so no benefit from debouncing on pan).
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Layers, ChevronDown, Check, AlertCircle, Flame } from 'lucide-react';
import type { BoundaryGeometry } from '../utils/boundaryUtils';
import { getWebConfig } from '../config';
import {
  addFireHazardLayers,
  setFireHazardVisibility,
  FIRE_HAZARD_SOURCE_ID,
  addActiveFireLayer,
  setActiveFireVisibility,
  startActiveFirePulse,
  ACTIVE_FIRE_SOURCE_ID,
  FIRE_HAZARD_UI_ENABLED,
} from './fireHazardLayers';
import { useFireHazardOverlay } from '../hooks/useFireHazardOverlay';
import { useActiveFirePerimeters } from '../hooks/useActiveFirePerimeters';

// Token is read inside the component body (not at module scope)
// because fetchWebConfig() may not have resolved yet at import time.
// ---------------------------------------------------------------------------
// Map style definitions — aligned with TenantMapDefaults.mapStyle values
// ---------------------------------------------------------------------------
type MapStyleKey = 'streets' | 'satellite' | 'hybrid';

interface MapStyleOption {
  id: MapStyleKey;
  label: string;
  url: string;
}

const MAP_STYLES: MapStyleOption[] = [
  { id: 'streets',   label: 'Streets',   url: 'mapbox://styles/mapbox/streets-v12' },
  { id: 'satellite', label: 'Satellite', url: 'mapbox://styles/mapbox/satellite-v9' },
  { id: 'hybrid',    label: 'Hybrid',    url: 'mapbox://styles/mapbox/satellite-streets-v12' },
];

const DEFAULT_STYLE = MAP_STYLES[0];
const DEFAULT_CENTER: [number, number] = [-98.5795, 39.8283]; // geographic center of CONUS
const DEFAULT_ZOOM = 4;

const BOUNDARY_SOURCE = 'dept-boundary';
const BOUNDARY_FILL   = 'dept-boundary-fill';
const BOUNDARY_LINE   = 'dept-boundary-line';

const ACTIVE_FIRE_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export interface DepartmentMapViewProps {
  center?: [number, number] | null;
  zoom?: number | null;
  mapStyle?: MapStyleKey | null;
  boundary?: BoundaryGeometry | null;
  /** Called when the user pans/zooms and the view settles */
  onCenterChange?: (center: [number, number]) => void;
  onZoomChange?: (zoom: number) => void;
  /**
   * Called whenever the map's active style changes, whether from the
   * in-map style switcher (handleStyleChange) or the mapStyle prop being
   * applied. Optional — if the caller doesn't pass mapStyle as a truly
   * controlled value, there's nothing to keep in sync. If it DOES treat
   * mapStyle as controlled, wiring this back into that same state is
   * what keeps mapStyle from permanently diverging from what the map
   * actually shows after an in-map switch: lastSyncedMapStylePropRef only
   * re-applies mapStyle on a genuine prop CHANGE, so if the parent's
   * mapStyle value doesn't move when the user switches in-map, the
   * parent can never force the map back to a since-diverged style later
   * — see this file's Architecture note on the fixed self-reverting race
   * for why lastSyncedMapStylePropRef exists and why it can't just be
   * removed to "fix" this instead.
   */
  onStyleChange?: (styleId: MapStyleKey) => void;
  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function DepartmentMapView({
  center,
  zoom,
  mapStyle,
  boundary,
  onCenterChange,
  onZoomChange,
  onStyleChange,
  className = '',
}: DepartmentMapViewProps) {
  const MAPBOX_TOKEN = getWebConfig().mapboxToken;
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef       = useRef<mapboxgl.Map | null>(null);
  // Set true for the duration of a programmatic map.resize() call (see the
  // ResizeObserver below) so the moveend handlers registered further down
  // this effect can distinguish "resize() fired a synthetic move/moveend"
  // from a genuine user pan/zoom or an intentional flyTo/fitBounds camera
  // change — resize()'s own move/moveend must NOT invoke onCenterChange/
  // onZoomChange or trigger a hazard-overlay refetch, since it carries no
  // new viewport information (the center/zoom are whatever they already
  // were, possibly still the un-fitted default).
  const isProgrammaticResizeRef = useRef(false);
  const { fetchOverlay: fetchFireHazardOverlay } = useFireHazardOverlay();
  const { fetchActiveFires: fetchActiveFirePerimeters } = useActiveFirePerimeters();
  const [mapReady, setMapReady]         = useState(false);
  const [activeStyle, setActiveStyle]   = useState<MapStyleKey>(
    (mapStyle && MAP_STYLES.find((s) => s.id === mapStyle)) ? mapStyle : DEFAULT_STYLE.id,
  );
  // Tracks the last mapStyle PROP value the sync effect below has already
  // reacted to, so it can distinguish a genuine external prop change from
  // activeStyle changing on its own (e.g. via the in-map style switcher)
  // — see that effect and this file's Architecture note.
  const lastSyncedMapStylePropRef = useRef(mapStyle);
  const [layerMenuOpen, setLayerMenuOpen] = useState(false);

  // Fire hazard overlay — rendered below the boundary layer (added before
  // it in the map's layer registration order) so the department boundary
  // highlight always shows on top. See ./fireHazardLayers.ts's
  // fileoverview for the full rationale.
  //
  // NOTE: This overlay and its toggle are currently HIDDEN (not rendered)
  // and disabled by default (tied to FIRE_HAZARD_UI_ENABLED) per customer
  // request.
  const [fireHazardVisible, setFireHazardVisible] = useState(FIRE_HAZARD_UI_ENABLED);
  const fireHazardVisibleRef = useRef(fireHazardVisible);
  fireHazardVisibleRef.current = fireHazardVisible;
  const fireHazardLoadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Separate timer for the active-fire overlay's own initial-load defer —
  // see addFireHazardAndActiveFireLayers's FIXED note on why the very
  // first refresh after adding a source can't be called synchronously.
  const activeFireInitialLoadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // In-flight dedupe guards for the two overlay refreshes below — mirrors
  // mobile-pwa MapView's isActiveFireLoadingRef pattern. Without these,
  // the moveend/zoomend debounce, the 5-minute active-fire interval, the
  // toggle handler, and style-change re-adds can all fire concurrently,
  // producing overlapping requests to the same endpoint that also consume
  // the tenant's shared rate-limit budget faster than necessary.
  const isFireHazardOverlayLoadingRef = useRef(false);
  const isActiveFireLoadingRef = useRef(false);
  // Active fire perimeters (Phase 5.9) — tied to the SAME toggle button as
  // the hazard zone layers above (this app has one "Fire Hazard" toggle,
  // not mobile-pwa's granular per-layer LayerManager — see
  // fireHazardLayers.ts's Architecture note). Holds the cleanup function
  // from startActiveFirePulse so the interval can be stopped on
  // toggle-off/unmount.
  const activeFirePulseStopRef = useRef<(() => void) | null>(null);

  // ---------------------------------------------------------------------------
  // Boundary helpers
  // ---------------------------------------------------------------------------
  const addBoundary = useCallback(() => {
    const map = mapRef.current;
    if (!map || !boundary) return;

    const geojson: GeoJSON.Feature = {
      type: 'Feature',
      properties: {},
      geometry: boundary as GeoJSON.Geometry,
    };

    if (map.getSource(BOUNDARY_SOURCE)) {
      (map.getSource(BOUNDARY_SOURCE) as mapboxgl.GeoJSONSource).setData(geojson);
    } else {
      map.addSource(BOUNDARY_SOURCE, { type: 'geojson', data: geojson });
      map.addLayer({
        id: BOUNDARY_FILL,
        type: 'fill',
        source: BOUNDARY_SOURCE,
        paint: { 'fill-color': '#ea580c', 'fill-opacity': 0.15 },
      });
      map.addLayer({
        id: BOUNDARY_LINE,
        type: 'line',
        source: BOUNDARY_SOURCE,
        paint: { 'line-color': '#ea580c', 'line-width': 2 },
      });
    }
  }, [boundary]);

  const removeBoundary = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    if (map.getLayer(BOUNDARY_LINE)) map.removeLayer(BOUNDARY_LINE);
    if (map.getLayer(BOUNDARY_FILL)) map.removeLayer(BOUNDARY_FILL);
    if (map.getSource(BOUNDARY_SOURCE)) map.removeSource(BOUNDARY_SOURCE);
  }, []);

  // ---------------------------------------------------------------------------
  // Fire hazard overlay helpers
  // ---------------------------------------------------------------------------

  // Fetch the fire hazard overlay for the current viewport and feed it into
  // the fire-hazard-source. Mirrors mobile-pwa MapView's
  // refreshFireHazardOverlay — failures leave the existing source data
  // untouched (non-critical, decorative map layer) rather than clearing it.
  const refreshFireHazardOverlay = useCallback(async () => {
    const map = mapRef.current;
    if (!map || !fireHazardVisibleRef.current) return;
    if (isFireHazardOverlayLoadingRef.current) return;
    if (!navigator.onLine) return;
    if (!map.isStyleLoaded()) return;
    const source = map.getSource(FIRE_HAZARD_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
    if (!source) return;
    const b = map.getBounds();
    if (!b) return;
    isFireHazardOverlayLoadingRef.current = true;
    try {
      const data = await fetchFireHazardOverlay({
        west: b.getWest(),
        south: b.getSouth(),
        east: b.getEast(),
        north: b.getNorth(),
      });
      source.setData(data as unknown as GeoJSON.FeatureCollection);
    } catch (err) {
      console.warn('[DepartmentMapView] Failed to refresh fire hazard overlay:', err);
    } finally {
      isFireHazardOverlayLoadingRef.current = false;
    }
  }, []);

  // Fetch the nationwide active fire perimeter overlay (NOT viewport-
  // scoped, unlike the hazard zone overlay above — see @firesquirrel/core's
  // activeFirePerimeterQueryService fileoverview).
  const refreshActiveFires = useCallback(async () => {
    const map = mapRef.current;
    if (!map || !fireHazardVisibleRef.current) return;
    if (isActiveFireLoadingRef.current) return;
    if (!navigator.onLine) return;
    if (!map.isStyleLoaded()) return;
    const source = map.getSource(ACTIVE_FIRE_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
    if (!source) return;
    isActiveFireLoadingRef.current = true;
    try {
      const data = await fetchActiveFirePerimeters();
      source.setData(data as unknown as GeoJSON.FeatureCollection);
    } catch (err) {
      console.warn('[DepartmentMapView] Failed to refresh active fire perimeters:', err);
    } finally {
      isActiveFireLoadingRef.current = false;
    }
  }, []);

  // Toggle fire hazard layer visibility (hazard zones AND active fires
  // together — see fireHazardLayers.ts's Architecture note) — re-fetches
  // on re-enable so a stale/empty source (from before the toggle was on)
  // doesn't linger, and starts/stops the active-fire pulse animation.
  const handleToggleFireHazard = useCallback(() => {
    const map = mapRef.current;
    const next = !fireHazardVisible;
    setFireHazardVisible(next);
    // Update the ref synchronously, in the same tick — refreshFireHazardOverlay/
    // refreshActiveFires below are called BEFORE React re-renders and syncs
    // fireHazardVisibleRef.current from the new fireHazardVisible state (that
    // sync happens as a side effect of render, at line ~140). Without this,
    // both refresh functions' `if (!map || !fireHazardVisibleRef.current) return;`
    // guard would still see the PREVIOUS (false) value when re-enabling the
    // layer, silently dropping the refetch and leaving stale/empty map data
    // until the next unrelated pan/zoom.
    fireHazardVisibleRef.current = next;
    if (map) {
      setFireHazardVisibility(map, next);
      setActiveFireVisibility(map, next);
      if (next) {
        void refreshFireHazardOverlay();
        void refreshActiveFires();
        activeFirePulseStopRef.current?.();
        activeFirePulseStopRef.current = startActiveFirePulse(map);
      } else {
        activeFirePulseStopRef.current?.();
        activeFirePulseStopRef.current = null;
      }
    }
  }, [fireHazardVisible, refreshFireHazardOverlay, refreshActiveFires]);

  // ---------------------------------------------------------------------------
  // Fit to boundary bounds
  // ---------------------------------------------------------------------------
  const fitToBoundary = useCallback((b: BoundaryGeometry) => {
    const map = mapRef.current;
    if (!map) return;
    const allCoords: number[][] = [];
    const collect = (rings: number[][][]) => rings.forEach((r) => r.forEach((c) => allCoords.push(c)));
    if (b.type === 'Polygon') collect(b.coordinates);
    else b.coordinates.forEach((p) => collect(p));
    if (allCoords.length === 0) return;
    const bounds = allCoords.reduce(
      (acc, c) => acc.extend(c as [number, number]),
      new mapboxgl.LngLatBounds(allCoords[0] as [number, number], allCoords[0] as [number, number]),
    );
    map.fitBounds(bounds, { padding: 40, duration: 800, maxZoom: 18 });
  }, []);

  // Adds the fire hazard zone + active fire perimeter sources/layers, sets
  // their visibility from fireHazardVisibleRef, kicks off both refreshes,
  // and (re)starts the active-fire pulse if visible. Shared by the initial
  // 'load' handler, handleStyleChange's 'style.load' handler, and the
  // mapStyle-prop-sync effect's 'style.load' handler — all three need the
  // identical sequence because setStyle() destroys every custom
  // source/layer, and drift between the three copies would only surface
  // as a missing overlay on one specific style-change path. Callers are
  // still responsible for re-adding the boundary layer afterward (that
  // part is NOT shared, since the initial load handler doesn't call
  // addBoundary() directly — see the mapReady-driven boundary effect).
  const addFireHazardAndActiveFireLayers = useCallback((map: mapboxgl.Map) => {
    // DEFENSIVE: callers invoke this from a map.once('style.load', ...)
    // handler after setStyle() — Mapbox GL's diffed setStyle path has
    // historically had edge cases (documented upstream in mapbox-gl-js/
    // maplibre-gl-js issue trackers) where style.load either doesn't fire
    // at all for certain style-to-style transitions, or where a retained
    // source somehow survives a swap. addFireHazardLayers/addActiveFireLayer
    // are explicitly NOT idempotent (Mapbox's addLayer/addSource throw if
    // the id already exists), so guard on existence rather than assuming
    // this always runs against a guaranteed-clean map — a throw here would
    // otherwise silently abort re-adding EITHER overlay for the rest of
    // this style.load callback.
    if (!map.getSource(FIRE_HAZARD_SOURCE_ID)) {
      map.addSource(FIRE_HAZARD_SOURCE_ID, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      addFireHazardLayers(map);
    }
    setFireHazardVisibility(map, fireHazardVisibleRef.current);
    // FIXED: calling refreshFireHazardOverlay/refreshActiveFires
    // synchronously right here used to silently drop the very first
    // fetch. Both refresh functions bail out on `!map.isStyleLoaded()`
    // (an in-flight/offline guard added to dedupe overlapping requests —
    // see that guard's own comment), but a freshly `addSource()`'d
    // GeoJSON source has NOT finished its initial (async, off-main-thread)
    // load cycle in the same synchronous tick it was added — even though
    // the data is inline (no network fetch), Mapbox still processes it
    // asynchronously, so `isStyleLoaded()` reliably still returns false
    // immediately after `addSource()`. That silently dropped fetch left
    // the hazard-zone overlay blank until the next moveend/zoomend, and
    // the active-fire layer blank for up to 5 minutes (its ONLY other
    // trigger is the fixed interval below) — with an ACTIVE incident
    // potentially invisible on a freshly opened map the whole time.
    // Mirrors the same defer-by-300ms pattern already proven for
    // mobile-pwa's hazard-zone overlay (MapView.tsx's fireHazardLoadTimer)
    // rather than removing the isStyleLoaded() guard outright.
    if (fireHazardLoadTimer.current) clearTimeout(fireHazardLoadTimer.current);
    fireHazardLoadTimer.current = setTimeout(() => void refreshFireHazardOverlay(), 300);

    if (!map.getSource(ACTIVE_FIRE_SOURCE_ID)) {
      map.addSource(ACTIVE_FIRE_SOURCE_ID, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      addActiveFireLayer(map);
    }
    setActiveFireVisibility(map, fireHazardVisibleRef.current);
    if (activeFireInitialLoadTimer.current) clearTimeout(activeFireInitialLoadTimer.current);
    activeFireInitialLoadTimer.current = setTimeout(() => void refreshActiveFires(), 300);
    if (fireHazardVisibleRef.current) {
      activeFirePulseStopRef.current?.();
      activeFirePulseStopRef.current = startActiveFirePulse(map);
    }
  }, [refreshFireHazardOverlay, refreshActiveFires]);

  // ---------------------------------------------------------------------------
  // Initialize map (once)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!mapContainer.current || !MAPBOX_TOKEN) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;

    const initialStyle = MAP_STYLES.find((s) => s.id === activeStyle) ?? DEFAULT_STYLE;

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: initialStyle.url,
      center: center ?? DEFAULT_CENTER,
      zoom: zoom ?? DEFAULT_ZOOM,
      attributionControl: false,
    });

    map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-left');
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

    map.on('load', () => {
      // Fire hazard source/layers are added BEFORE the boundary layers
      // (added later, once mapReady) so the department boundary highlight
      // always renders on top — see fireHazardLayers.ts's fileoverview.
      if (FIRE_HAZARD_UI_ENABLED) {
        addFireHazardAndActiveFireLayers(map);
      }
      setMapReady(true);
    });

    map.on('moveend', () => {
      // Skip: this moveend was fired by our own map.resize() call (see the
      // ResizeObserver below), not a real user pan/zoom or an intentional
      // flyTo/fitBounds. Unlike map.getBounds() (see
      // debouncedRefreshFireHazard below), center/zoom genuinely do NOT
      // change from a resize — reporting them here would be redundant at
      // best and could misrepresent a resize as a user camera action to
      // whatever the parent does with onCenterChange/onZoomChange (e.g.
      // "Set from current view" state).
      if (isProgrammaticResizeRef.current) return;
      if (onCenterChange) {
        const c = map.getCenter();
        onCenterChange([c.lng, c.lat]);
      }
      if (onZoomChange) {
        onZoomChange(map.getZoom());
      }
    });

    const debouncedRefreshFireHazard = () => {
      // NOTE: registered below on BOTH moveend and zoomend, but only the
      // moveend registration fires (intentionally) from the
      // map.resize()-driven event dispatched by the ResizeObserver below —
      // resize() fires only movestart/move/resize/moveend, never
      // zoom/zoomend, since a resize never changes the zoom level. This
      // handler is deliberately NOT gated on isProgrammaticResizeRef.
      // Unlike center/zoom (unchanged by a resize, see the moveend handler
      // above), map.getBounds() DOES change when the container's pixel
      // size changes — a bigger
      // container shows strictly more geography at the same center/zoom —
      // and this overlay is keyed off bounds. Suppressing this refresh on
      // a resize would leave newly-revealed map area without hazard
      // shading until the next manual pan/zoom, including on the very
      // first corrective resize, since that is exactly the moment the
      // container's bounds become accurate for the first time.
      if (fireHazardLoadTimer.current) clearTimeout(fireHazardLoadTimer.current);
      fireHazardLoadTimer.current = setTimeout(() => void refreshFireHazardOverlay(), 300);
    };
    if (FIRE_HAZARD_UI_ENABLED) {
      map.on('moveend', debouncedRefreshFireHazard);
      map.on('zoomend', debouncedRefreshFireHazard);
    }

    // Active fires are NOT viewport-scoped, so refresh on a fixed interval
    // (matching NIFC's ~5 minute update cadence) instead of pan/zoom.
    let activeFireIntervalId: ReturnType<typeof setInterval> | undefined;
    if (FIRE_HAZARD_UI_ENABLED) {
      activeFireIntervalId = setInterval(() => { void refreshActiveFires(); }, ACTIVE_FIRE_REFRESH_INTERVAL_MS);
    }

    mapRef.current = map;

    // FIXED (defensive): verified against the actual pinned
    // mapbox-gl@3.20.0 source (this app's own package.json pin — NOT
    // 3.18.1, which tenant-web/mobile-pwa pin instead; re-verified
    // separately per-app rather than assuming the two versions match) —
    // Mapbox GL has NO container-level ResizeObserver of its own in
    // either version. Its `trackResize` option (default true) only wires
    // `window.addEventListener('resize', ...)`; it reacts to
    // the WINDOW resizing, not the container. `resize()` re-measures the
    // container and no-ops if unchanged, but nothing calls `resize()`
    // again after construction unless a window resize fires — a
    // container-only size change (e.g. an ancestor's flex/height chain
    // resolving on a later layout pass) is invisible to Mapbox entirely,
    // which can leave the map rendered into a stale/0-height container
    // with no error. Confirmed to manifest this way for
    // SlideoutMapView.tsx's identical construction pattern when rendered
    // inside tenant-web's full-page LocationDetailPage route (vs. its
    // slide-out-panel usage, which has a different, working ancestor
    // height chain and/or a window resize along the way). Applied the
    // same fix here defensively, since this component has the identical
    // construction pattern and is used in more than one ancestor context
    // too. Adding our OWN ResizeObserver on the container — a mechanism
    // Mapbox has no equivalent of — calls map.resize() whenever the
    // container's actual size changes, regardless of whether a window
    // resize ever happens.
    // IMPORTANT: ResizeObserver.observe() always delivers one initial
    // callback shortly after observation begins, even if the size never
    // changes again afterward — that FIRST callback is what actually
    // rescues the blank-map case (it's the first and only
    // re-measurement this container will ever get, since no window
    // resize occurs to trigger Mapbox's own mechanism). Do not
    // "optimize" this by skipping the first entry or diffing against a
    // cached size — that would silently reintroduce the blank map.
    const resizeObserver = new ResizeObserver(() => {
      // resize() fires its own move/movestart/moveend/resize events
      // synchronously (verified against Mapbox GL's implementation — it
      // calls _update()/fire() directly, not via a deferred animation
      // frame) — flag it as programmatic so the moveend handler
      // registered above ignores it (see isProgrammaticResizeRef's
      // declaration), then clear the flag synchronously right after.
      // Deliberately NOT deferred via requestAnimationFrame: rAF does not
      // run while the document is hidden/backgrounded, so if a
      // ResizeObserver callback were ever delivered without a subsequent
      // frame, the flag would stay true indefinitely and silently
      // suppress every later moveend-driven onCenterChange/onZoomChange
      // call for the life of the map instance. A synchronous clear has no
      // such dependency, since it doesn't rely on anything running later.
      // Wrapped in try/finally so a throwing resize() (unlikely, but
      // possible) can't leave the flag stuck true forever either.
      isProgrammaticResizeRef.current = true;
      try {
        map.resize();
      } finally {
        isProgrammaticResizeRef.current = false;
      }
    });
    resizeObserver.observe(mapContainer.current);

    return () => {
      resizeObserver.disconnect();
      if (fireHazardLoadTimer.current) clearTimeout(fireHazardLoadTimer.current);
      if (activeFireInitialLoadTimer.current) clearTimeout(activeFireInitialLoadTimer.current);
      if (activeFireIntervalId) clearInterval(activeFireIntervalId);
      // Stop the pulse interval BEFORE map.remove() — otherwise its next
      // tick would call setPaintProperty on a destroyed map instance.
      activeFirePulseStopRef.current?.();
      activeFirePulseStopRef.current = null;
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Apply boundary once map is ready
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!mapRef.current || !mapReady) return;
    if (boundary) {
      addBoundary();
      fitToBoundary(boundary);
    } else {
      removeBoundary();
    }
  }, [mapReady, boundary, addBoundary, removeBoundary, fitToBoundary]);

  // ---------------------------------------------------------------------------
  // React to center/zoom prop changes (when user edits the inline fields)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!mapRef.current || !mapReady) return;
    if (boundary) return; // boundary takes priority
    if (center) {
      mapRef.current.flyTo({ center, zoom: zoom ?? DEFAULT_ZOOM, duration: 800 });
    }
  }, [center, zoom, mapReady, boundary]);

  // ---------------------------------------------------------------------------
  // Style switcher
  // ---------------------------------------------------------------------------
  const handleStyleChange = useCallback((style: MapStyleOption) => {
    if (!mapRef.current || style.id === activeStyle) {
      setLayerMenuOpen(false);
      return;
    }
    const map = mapRef.current;
    map.setStyle(style.url);
    map.once('style.load', () => {
      // setStyle() clears all custom sources/layers — re-add fire hazard
      // BEFORE the boundary (layer order) and re-fetch its data (the new
      // source starts empty), matching the initial 'load' handler's order.
      if (FIRE_HAZARD_UI_ENABLED) {
        addFireHazardAndActiveFireLayers(map);
      }
      if (boundary) addBoundary();
    });
    setActiveStyle(style.id);
    setLayerMenuOpen(false);
    onStyleChange?.(style.id);
  }, [activeStyle, boundary, addBoundary, addFireHazardAndActiveFireLayers, onStyleChange]);

  // ---------------------------------------------------------------------------
  // Sync mapStyle prop → internal state
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // FIXED: guard on the map actually existing/being ready BEFORE
    // touching lastSyncedMapStylePropRef — a mapStyle prop change that
    // arrives while the map is still starting up (mapRef.current is null
    // during the async MAPBOX_TOKEN resolution window) must NOT be marked
    // as consumed, or it would be permanently dropped: none of this
    // effect's other dependencies change again once the map becomes
    // ready, so a ref update here (before this guard) would mean the
    // effect never gets another chance to apply that style. mapReady is
    // in the dependency array below specifically so this effect re-runs
    // once the map transitions from not-ready to ready, retrying with
    // whatever mapStyle value is current AT THAT POINT.
    if (!mapRef.current || !mapReady) return;

    // Only react to the mapStyle PROP itself actually changing (a genuine
    // external update) — not to activeStyle changing, even though
    // activeStyle must stay in this effect's dependency array (it's read
    // below) and DOES change as a side effect of handleStyleChange. See
    // this file's Architecture note on the fixed self-reverting race.
    const propChanged = mapStyle !== lastSyncedMapStylePropRef.current;
    lastSyncedMapStylePropRef.current = mapStyle;
    if (!propChanged) return;

    if (!mapStyle || mapStyle === activeStyle) return;
    const opt = MAP_STYLES.find((s) => s.id === mapStyle);
    if (!opt) return;
    const map = mapRef.current;
    map.setStyle(opt.url);
    map.once('style.load', () => {
      if (FIRE_HAZARD_UI_ENABLED) {
        addFireHazardAndActiveFireLayers(map);
      }
      if (boundary) addBoundary();
    });
    setActiveStyle(opt.id);
  }, [mapStyle, activeStyle, boundary, addBoundary, addFireHazardAndActiveFireLayers, mapReady]);

  // ---------------------------------------------------------------------------
  // No token — render placeholder
  // ---------------------------------------------------------------------------
  if (!MAPBOX_TOKEN) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 rounded-lg ${className}`}>
        <div className="flex flex-col items-center gap-2 text-gray-400 text-sm p-6 text-center">
          <AlertCircle className="h-8 w-8" />
          <span>Map unavailable — VITE_MAPBOX_TOKEN not set</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative rounded-lg overflow-hidden ${className}`}>
      <div ref={mapContainer} className="absolute inset-0" />

      {/* Fire hazard overlay toggle — gated behind FIRE_HAZARD_UI_ENABLED (Phase 5.9 Sprint 5) */}
      {FIRE_HAZARD_UI_ENABLED && (
        <div className="absolute top-2 left-28 z-10">
          <button
            type="button"
            onClick={handleToggleFireHazard}
            className={`flex items-center justify-center h-[26px] w-[26px] rounded shadow-sm border border-gray-200 backdrop-blur-sm transition-colors ${fireHazardVisible ? 'bg-orange-50/90' : 'bg-white/90 hover:bg-white'}`}
            title={fireHazardVisible ? 'Hide fire hazard zones and active fires' : 'Show fire hazard zones and active fires'}
            aria-pressed={fireHazardVisible}
          >
            <Flame className={`h-3 w-3 ${fireHazardVisible ? 'text-orange-500' : 'text-gray-400'}`} />
          </button>
        </div>
      )}

      {/* Layer/style picker */}
      <div className="absolute top-2 left-2 z-10">
        <button
          type="button"
          onClick={() => setLayerMenuOpen((v) => !v)}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-white/90 backdrop-blur-sm rounded shadow-sm border border-gray-200 hover:bg-white transition-colors"
        >
          <Layers className="h-3 w-3 text-gray-600" />
          <span className="text-gray-700">{MAP_STYLES.find((s) => s.id === activeStyle)?.label}</span>
          <ChevronDown className="h-3 w-3 text-gray-500" />
        </button>
        {layerMenuOpen && (
          <div className="absolute top-full left-0 mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
            {MAP_STYLES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => handleStyleChange(s)}
                className="w-full flex items-center justify-between px-3 py-1.5 text-xs hover:bg-gray-50 text-gray-700"
              >
                <span>{s.label}</span>
                {s.id === activeStyle && <Check className="h-3 w-3 text-orange-500" />}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
